import { useCallback, useEffect, useState } from 'react';

import { errorKey, type AuthApi, type Session } from '../auth/api';
import { useI18n } from '../i18n/I18nProvider';
import { Button } from '../ui/Button';
import { DownlineSheet } from '../referral/DownlineSheet';
import type { ChestResult, ClaimResult, ReferralSummary } from '../referral/api';
import type { GameApi, GameProfile } from '../profile/api';
import { copyText } from '../ui/clipboard';
import '../referral/referral.css';

/**
 * 分享 / 三级代理。
 *
 * 和个人页、充值页同一个做法：**两边各拉各的**。
 *
 *   account   邀请码、链接、各级人数、名单、规则   —— 纯读
 *   gateway   领返点、开宝箱                      —— 要写钱包，只能它来
 *
 * 钱包只有一个写者（ADR 0001）：下级充值时只在库里记了一行待领的账，
 * 上级点"领取"才真的进钱包。当场发的话就是写另一个玩家的钱包，
 * 而那个玩家可能绑在另一台实例上、甚至不在线。
 *
 * **界面上一条规则都不重算**：能不能领、还差几个人、比例是多少，
 * 全是服务端给的。自己拼的话，后台改了配置那条规则就悄悄过期了，
 * 而界面还理直气壮地显示着旧数字。
 */
export function AgentTab(
  { api, game, session, onNeedLogin }:
  { api: AuthApi; game: GameApi; session: Session | null; onNeedLogin(): void },
) {
  const { t, i18n } = useI18n();
  const [sum, setSum] = useState<ReferralSummary | null>(null);
  const [gp, setGp] = useState<GameProfile | null>(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [copied, setCopied] = useState(false);
  const [level, setLevel] = useState(0);      // 0 = 名单没打开
  // 幂等键。**只在成功之后换** —— 失败时换掉的话，重试会被当成新的一次领取。
  const [reqId, setReqId] = useState(() => crypto.randomUUID());

  const load = useCallback(async () => {
    if (!session) return;
    const [a, g] = await Promise.allSettled([
      api.referralSummary(session.token), game.profile(session.token)]);
    if (a.status === 'fulfilled') setSum(a.value);
    if (g.status === 'fulfilled') setGp(g.value);
    const bad = [a, g].find((r) => r.status === 'rejected');
    setErr(bad ? t(errorKey((bad as PromiseRejectedResult).reason)) : '');
  }, [api, game, session, t]);

  useEffect(() => { void load(); }, [load]);

  const money = (n: number | string) => {
    const v = typeof n === 'string' ? Number(n) : n;
    return gp ? i18n.money(v, gp.cur, { symbol: gp.sym, decimals: gp.dec })
              : i18n.num(v);
  };

  async function copy() {
    if (!sum) return;
    const text = sum.link || sum.code;
    if (!(await copyText(text))) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  async function claim() {
    if (!session) return;
    setBusy('claim'); setErr(''); setOk('');
    try {
      const r = await game.call<ClaimResult>('referral_claim', session.token, { reqId });
      setReqId(crypto.randomUUID());          // 成功了才换
      // 没有免费局时别显示 "+0 局" —— 那是句废话，而废话会让人以为哪里出了错
      setOk(r.freeSpins
        ? t('ref.claimed', { v: money(r.amount), n: r.freeSpins })
        : t('ref.claimedCash', { v: money(r.amount) }));
      await load();
    } catch (e) {
      setErr(t(errorKey(e)));
      await load();
    } finally {
      setBusy('');
    }
  }

  async function openChest(need: number) {
    if (!session) return;
    setBusy(`c${need}`); setErr(''); setOk('');
    try {
      const r = await game.call<ChestResult>('chest_open', session.token, { need });
      setOk(r.reward.freeSpins
        ? t('ref.chestGot', { v: money(r.reward.cash), n: r.reward.freeSpins })
        : t('ref.chestGotCash', { v: money(r.reward.cash) }));
      await load();
    } catch (e) {
      setErr(t(errorKey(e)));
      await load();
    } finally {
      setBusy('');
    }
  }

  if (!session) {
    return (
      <section className="ref">
        <p className="ref__empty">{t('ref.needLogin')}</p>
        <Button variant="primary" onClick={onNeedLogin}>{t('action.login')}</Button>
      </section>
    );
  }

  const c = sum?.counts;
  const rules = sum?.rules;
  const pending = sum?.pending;

  return (
    <section className="ref">
      {err && <p className="ref__err">{err}</p>}
      {ok && <p className="ref__ok">{ok}</p>}

      {/* ---------------- 分享链接 ---------------- */}
      <div className="ref__card ref__share">
        <h3>{t('ref.myLink')}</h3>
        <div className="ref__linkrow">
          <input readOnly value={sum?.link || sum?.code || ''}
                 onFocus={(e) => e.currentTarget.select()} />
          <Button variant="primary" onClick={copy} disabled={!sum}>
            {t(copied ? 'ref.copied' : 'ref.copy')}
          </Button>
        </div>
        {sum && <p className="ref__code">{t('ref.code', { v: sum.code })}</p>}
      </div>

      {/* ---------------- 待领返点 ---------------- */}
      <div className="ref__card ref__claim">
        <div>
          <h3>{t('ref.pending')}</h3>
          <b className="ref__amount">{money(pending?.amount ?? 0)}</b>
          {!!pending?.freeSpins && (
            <span className="ref__spins">+{t('ref.spins', { n: pending.freeSpins })}</span>
          )}
        </div>
        {/* 有没有可领的**由服务端的数字决定**，不由前端拼 */}
        <Button variant="primary" onClick={claim}
                disabled={busy === 'claim' || !pending?.count}>
          {t('ref.claim')}
        </Button>
      </div>

      {/* ---------------- 人数 ---------------- */}
      <div className="ref__card">
        <h3>{t('ref.team')}</h3>
        <div className="ref__grid">
          {[1, 2, 3].map((lv) => (
            <button key={lv} type="button" className="ref__cell"
                    onClick={() => setLevel(lv)}>
              <span className="ref__lv">{t('ref.level', { n: lv })}</span>
              <b>{c ? c[`d${lv}` as 'd1'] : 0}</b>
              <span className="ref__eff">
                {t('ref.effN', { n: c ? c[`e${lv}` as 'e1'] : 0 })}
              </span>
            </button>
          ))}
        </div>
        <p className="ref__hint">{t('ref.tapForList')}</p>
      </div>

      {/* ---------------- 宝箱 ---------------- */}
      <div className="ref__card">
        <h3>{t('ref.chests')}</h3>
        <div className="ref__chests">
          {(sum?.chests ?? []).map((ch) => (
            <div key={ch.need}
                 className={`ref__chest${ch.claimed ? ' is-done' : ch.unlocked ? ' is-open' : ''}`}>
              <span className="ref__chestNeed">{t('ref.needN', { n: ch.need })}</span>
              <b>{money(ch.cash)}</b>
              {!!ch.freeSpins && <span>+{t('ref.spins', { n: ch.freeSpins })}</span>}
              {ch.claimed
                ? <span className="ref__chestState">{t('ref.claimedOne')}</span>
                : ch.canClaim
                  ? (
                    <Button variant="primary" disabled={busy === `c${ch.need}`}
                            onClick={() => openChest(ch.need)}>
                      {t('ref.open')}
                    </Button>
                    )
                  // "还差几个"也是服务端给的
                  : <span className="ref__chestState">{t('ref.leftN', { n: ch.left })}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* ---------------- 规则 ---------------- */}
      {rules && (
        <div className="ref__card ref__rules">
          <h3>{t('ref.rules')}</h3>
          <ol>
            <li>{t('ref.r1')}</li>
            <li>{t('ref.r2', { d: money(rules.effective.depositMin),
                               b: money(rules.effective.betMin) })}</li>
            <li>{t('ref.r3', { n: rules.levels })}</li>
            <li>{t('ref.r4')}</li>
          </ol>
          <table className="ref__table">
            <thead>
              <tr><th>{t('ref.tLevel')}</th><th>{t('ref.tRate')}</th><th>{t('ref.tSpins')}</th></tr>
            </thead>
            <tbody>
              {rules.rules.map((r) => (
                <tr key={r.level}>
                  <td>{t('ref.level', { n: r.level })}</td>
                  <td>{i18n.num(r.ratePercent)}%</td>
                  <td>{r.freeSpins || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ref__ver">{t('ref.version', { v: rules.configVersion })}</p>
        </div>
      )}

      <DownlineSheet open={level > 0} level={level || 1} api={api}
                     session={session} onClose={() => setLevel(0)} />
    </section>
  );
}
